import { FC, ReactNode, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import * as S from './style'

interface Item {
  label: string
  component: FC<any>
  props?: any
}

interface TabProps {
  items: Item[]
  children?: ReactNode
  activeIndex?: number
  onTabChange?: (index: number) => void
}

const Tab: FC<TabProps> = ({ items, children, activeIndex, onTabChange }) => {
  const { state, pathname } = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([])
  const [sliderDimensions, setSliderDimensions] = useState({
    width: 0,
    left: 0,
    shouldAnimate: false,
  })

  const tabParam = searchParams.get('tab')
  const urlActiveIndex = tabParam
    ? items.findIndex(
        (item) => item.label.toLowerCase() === tabParam.toLowerCase(),
      )
    : 0
  const safeUrlActiveIndex = urlActiveIndex === -1 ? 0 : urlActiveIndex
  const effectiveActiveIndex =
    typeof activeIndex === 'number' ? activeIndex : safeUrlActiveIndex

  // Update slider position when active tab changes
  useEffect(() => {
    const activeTabRef = tabRefs.current[effectiveActiveIndex]
    if (activeTabRef) {
      const { offsetWidth: width, offsetLeft: left } = activeTabRef
      setSliderDimensions((prev) => ({
        width,
        left,
        shouldAnimate: prev.width !== 0, // Don't animate on initial render
      }))
    }
  }, [effectiveActiveIndex])

  const { component: Component, props = {} } = items[effectiveActiveIndex] || {}

  const handleTabClick = (label: string, index: number) => {
    if (typeof onTabChange === 'function') {
      onTabChange(index)
    } else {
      navigate(`${pathname}?tab=${encodeURIComponent(label)}`, { state })
    }
  }

  return (
    <>
      <S.StyleTabButtonContainer>
        {/* Sliding background element */}
        <S.SlidingBackground
          $width={sliderDimensions.width}
          $left={sliderDimensions.left}
          $shouldAnimate={sliderDimensions.shouldAnimate}
        />

        {items.map((item, index) => (
          <S.StyleTabButton
            key={index}
            ref={(el) => (tabRefs.current[index] = el)}
            $active={effectiveActiveIndex === index}
            onClick={() => handleTabClick(item.label, index)}
          >
            {item.label}
          </S.StyleTabButton>
        ))}
      </S.StyleTabButtonContainer>

      <S.StyleChildrenWrapper>
        {Component ? <Component {...props} /> : null}
      </S.StyleChildrenWrapper>
    </>
  )
}

export default Tab
